import { useEffect, useMemo, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { useAuth } from '../context/useAuth'
import { useDisplayName } from '../hooks/useDisplayName'
import { useWorkspace } from '../context/useWorkspace'
import { firebaseAuth } from '../services/firebase'
import {
  claimPendingInvite,
  declinePendingInvite,
  fetchInviteByToken,
} from '../services/inviteService'
import { HELPER_APK_DOWNLOAD_URL } from '../services/nativeUpdateService'
import type { PendingInvite } from '../types/invites'
import { getWrapperRole } from '../utils/wrapperRole'
import { buildMemberPath } from '../member/memberAppRouting'
import { Login } from './Auth/Login'
import { MiniLoader } from './Common'
import { Button } from './Common/Button'
import { NotFoundPage } from './NotFoundPage'

type InviteReviewPageProps = {
  inviteId: string
}

const goToHelperShell = () => {
  window.history.replaceState(null, '', '/helper/dashboard')
  window.dispatchEvent(new PopStateEvent('popstate'))
}

const goToWorkspace = (invite: PendingInvite) => {
  const wrapperRole = getWrapperRole()
  const slug = invite.workspaceSlug
  const target = invite.role === 'HELPER'
    ? '/helper/dashboard'
    : wrapperRole === 'member' && slug
      ? buildMemberPath({ workspace: slug, tab: 'for-you' })
      : slug
        ? `/workspace/${slug}/d`
        : '/'
  window.history.replaceState(null, '', target)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export const InviteReviewPage = ({ inviteId }: InviteReviewPageProps) => {
  const { currentUser, userProfile } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const { refreshWorkspaces } = useWorkspace()
  const [invite, setInvite] = useState<PendingInvite | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [busyAction, setBusyAction] = useState<'accept' | 'decline' | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!currentUser) {
      setInvite(null)
      setIsLoading(false)
      return () => {
        cancelled = true
      }
    }
    setIsLoading(true)
    void fetchInviteByToken(inviteId)
      .then((loaded) => {
        if (!cancelled) setInvite(loaded)
      })
      .catch((error) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : 'Could not load this invite.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentUser, inviteId])

  const claimingUser = useMemo(() => ({
    uid: currentUser?.uid ?? '',
    email: currentUser?.email ?? null,
    phoneE164: currentUser?.phoneNumber ?? null,
    displayName: displayName || currentUser?.email || currentUser?.phoneNumber || null,
  }), [currentUser, displayName])

  const emailMatches = Boolean(invite?.type === 'EMAIL' && currentUser?.email && currentUser.email.toLowerCase() === invite.recipient.toLowerCase())
  const phoneMatches = Boolean(invite?.type === 'PHONE' && currentUser?.phoneNumber && currentUser.phoneNumber === invite.recipient)
  const linkInvite = invite?.type === 'LINK'
  const recipientMatches = Boolean(linkInvite || emailMatches || phoneMatches)
  const canAct = Boolean(currentUser && invite && invite.status === 'PENDING' && recipientMatches)
  const canDecline = Boolean(canAct && invite?.type !== 'LINK')
  // Helper surfaces are Android-only — accepting on desktop would just bounce
  // into AppOnlyRouteError. Steer helpers to the APK first so the accept flow
  // completes inside the app where the shell actually works.
  const isNative = Capacitor.isNativePlatform()
  const helperOnWeb = Boolean(invite && invite.role === 'HELPER' && !isNative)

  const accept = async () => {
    if (!invite || !canAct) return
    setBusyAction('accept')
    setMessage(null)
    try {
      await claimPendingInvite(invite.id, claimingUser)
      await firebaseAuth.currentUser?.getIdToken(true)
      await refreshWorkspaces()
      if (invite.role === 'HELPER') goToHelperShell()
      else goToWorkspace(invite)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not accept this invite.')
    } finally {
      setBusyAction(null)
    }
  }

  const decline = async () => {
    if (!invite || !canAct) return
    setBusyAction('decline')
    setMessage(null)
    try {
      await declinePendingInvite(invite.id, claimingUser)
      setInvite({ ...invite, status: 'DECLINED' })
      setMessage('Invite declined.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not decline this invite.')
    } finally {
      setBusyAction(null)
    }
  }

  if (!currentUser) {
    return <Login />
  }

  // Any failed fetch, non-existent token, terminal-state invite, OR a real
  // invite whose intended recipient doesn't match the signed-in account
  // collapses to the same "URL doesn't exist" surface. A URL guesser never
  // learns that /invite-review/{token} was a live route or which account it
  // was addressed to. Legitimate invitees whose link was revoked/claimed or
  // who need to switch accounts hear about it from the admin who invited
  // them, not from a page that confirms the URL existed.
  const inviteIsUnusable = !isLoading && (!invite || invite.status !== 'PENDING' || !recipientMatches)
  if (inviteIsUnusable) {
    return <NotFoundPage path={typeof window !== 'undefined' ? window.location.pathname : `/invite-review/${inviteId}`} />
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--bg)] p-4 text-[var(--text)]">
      <section className="w-full max-w-md rounded-lg border border-[var(--border)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-card)]">
        <p className="settingsEyebrow">{invite?.role === 'HELPER' ? 'Helper invite' : 'Workspace invite'}</p>
        <h1 className="mt-2 text-2xl font-semibold text-[var(--text-strong)]">
          {isLoading ? <MiniLoader size="sm" label="Loading invite" /> : invite?.workspaceName ?? 'Invite unavailable'}
        </h1>
        <p className="mt-2 text-sm text-[var(--muted)]">
          Review this invitation before joining the workspace.
        </p>

        <div className="mt-5 grid gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3 text-sm">
          <div className="flex justify-between gap-3"><span className="text-[var(--muted)]">Role</span><strong>{invite?.role ?? 'Unknown'}</strong></div>
          <div className="flex justify-between gap-3">
            <span className="text-[var(--muted)]">{invite?.type === 'PHONE' ? 'Phone' : invite?.type === 'EMAIL' ? 'Email' : 'Invite'}</span>
            <strong>{invite?.type === 'LINK' ? 'Shared link' : invite?.recipient ?? currentUser?.phoneNumber ?? currentUser?.email ?? 'Not available'}</strong>
          </div>
          <div className="flex justify-between gap-3"><span className="text-[var(--muted)]">Status</span><strong>{invite?.status ?? (isLoading ? <MiniLoader size="xs" label="Loading" inline /> : 'Unknown')}</strong></div>
          {invite?.invitedByName ? <div className="flex justify-between gap-3"><span className="text-[var(--muted)]">Invited by</span><strong>{invite.invitedByName}</strong></div> : null}
        </div>

        {/* Failure/wrong-recipient branches removed — inviteIsUnusable above
            now redirects any non-PENDING or mis-addressed invite to
            NotFoundPage so we never confirm the URL to a URL guesser. */}

        {message ? <p className="mt-4 text-sm text-[var(--text)]">{message}</p> : null}

        {helperOnWeb ? (
          <div className="mt-5 rounded-lg border border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[var(--success-soft)] p-3 text-sm text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]" data-testid="invite-helper-app-cta">
            <p className="font-semibold">Install the Helper app to accept</p>
            <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--success)_50%,var(--text-strong))]">
              The Helper shell only runs inside the Android app. Install it on your phone, sign in
              with this number, and this invite will be waiting.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={HELPER_APK_DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="themePrimaryButton"
                data-testid="invite-helper-app-download"
              >
                Download Helper app
              </a>
              <a
                href="/download"
                className="topbarPill"
              >
                Install instructions
              </a>
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-2">
          <Button variant="primary" size="md" disabled={!canAct || busyAction !== null} onClick={() => void accept()}>
            {busyAction === 'accept' ? <MiniLoader size="xs" label="Accepting" inline /> : helperOnWeb ? 'Accept anyway (opens on this device)' : 'Accept and Join'}
          </Button>
          {invite?.type !== 'LINK' ? (
            <Button variant="tertiary" size="sm" disabled={!canDecline || busyAction !== null} onClick={() => void decline()}>
              {busyAction === 'decline' ? <MiniLoader size="xs" label="Declining" inline /> : 'Decline'}
            </Button>
          ) : null}
        </div>
      </section>
    </main>
  )
}
